import gql from "graphql-tag";
import {
  CartCarouselsResult,
  ProductGender,
  CarouselRecommendTypeInput,
} from "src/types/graphql.d";
import { ApolloError } from "@apollo/client";
import { useQuery } from "@apollo/react-hooks";
import { useFeatureFlags } from "./useFeatureFlags";

interface CartCarouselsVars {
  shop_type: ProductGender;
  limit: number;
  carousel_recommend_type?: CarouselRecommendTypeInput;
}

interface CartCarouselsResponse {
  cartCarousels: CartCarouselsResult | undefined;
}

interface CartCarouselsHook {
  data: CartCarouselsResponse | undefined;
  loading: boolean;
  error: ApolloError | undefined;
}

export const CART_CAROUSELS_QUERY = gql`
  query cartCarousels(
    $shop_type: ProductGender!
    $limit: Int!
    $carousel_recommend_type: CarouselRecommendTypeInput
  ) {
    cartCarousels(
      inputs: {
        shop_type: $shop_type
        limit: $limit
        carousel_recommend_type: $carousel_recommend_type
      }
    ) {
      carousels {
        id
        title
        allow_add_to_cart
        target_link
        target_type
        response_source_type
        products {
          id
          name
          slug
          price
          regular_price
          brand {
            id
            name
            designer_brand
          }
          thumbnail
          labels {
            type
            value
            color
            priority
          }
          available_sizes
          available_sizes_label
          currency {
            code
            symbol
            position
          }
          gate {
            level
          }
          images {
            url
            device
            shop_type
          }
          user_wishlist_item_id
        }
      }
    }
  }
`;

const DEFAULT_LIMIT = 12;
const useCartCarousels = ({
  shopType,
  limit = DEFAULT_LIMIT,
  carousel_recommend_type,
}: {
  shopType: ProductGender;
  limit?: number;
  carousel_recommend_type?: CarouselRecommendTypeInput;
}): CartCarouselsHook => {
  const { cartYMALCarouselRecommender } = useFeatureFlags();
  const { data, loading, error } = useQuery<
    CartCarouselsResponse,
    CartCarouselsVars
  >(CART_CAROUSELS_QUERY, {
    variables: {
      shop_type: shopType,
      limit,
      carousel_recommend_type: {
        cart_cross_sell_carousel:
          typeof cartYMALCarouselRecommender === "string"
            ? cartYMALCarouselRecommender
            : null,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  return {
    data,
    loading,
    error,
  };
};

export default useCartCarousels;
